import { availabilitySellTripSelectionSuccess } from '@actions/availability.action';
import {
  BookingActionTypes,
  bookingGetBookingAfterPassengerUpdateSuccess,
  bookingGetBookingSuccess,
  bookingRetrieveBookingSuccess,
  bookingStoreBooking,
} from '@actions/booking.action';
import {
  extrasClearTotalSelectionsState,
  extrasStoreExtrasUpdateRequest,
  extrasStoreExtrasUpdateRequestAllPax,
} from '@actions/extras.action';
import { ExtrasSsrsEnum } from '@constants/extras.constants';
import { N0SsrPassengerUpdateRequest } from '@models/requests/ssr-passenger-update-request.model';
import { createReducer, on, Action } from '@ngrx/store';

export interface State {
  totalSelections: N0SsrPassengerUpdateRequest[] | null;
}

export const INITIAL_STATE: State = {
  totalSelections: null,
};

const reducer = createReducer(
  INITIAL_STATE,
  on(
    bookingGetBookingSuccess,
    bookingRetrieveBookingSuccess,
    bookingGetBookingAfterPassengerUpdateSuccess,
    bookingStoreBooking,
    (state, action): State => {
      const booking =
        action.type === BookingActionTypes.storeBooking
          ? action.payload
          : action.booking;

      return {
        ...state,
        totalSelections:
          booking.extras?.map((j) => {
            return {
              journeyKey: j.journeyKey,
              passengers: j.passengerExtras.map((p) => ({
                passengerKey: p.passengerKey,
                ssrs: p.extras.map((b) => ({
                  ssrCode: b.ssrCode,
                  count: b.totalCount,
                })),
              })),
            };
          }) ?? state.totalSelections,
      };
    },
  ),

  on(extrasStoreExtrasUpdateRequest, (state, action): State => {
    if (!state.totalSelections) {
      return {
        ...state,
        totalSelections: [action.payload],
      };
    }

    const journeySelection = state.totalSelections.find(
      (s) => s.journeyKey === action.payload.journeyKey,
    );
    if (!journeySelection) {
      return {
        ...state,
        totalSelections: state.totalSelections.concat(action.payload),
      };
    }

    return {
      ...state,
      totalSelections: state.totalSelections.map((s) => {
        if (
          s.journeyKey !== action.payload.journeyKey ||
          !action.payload.passengers[0]
        ) {
          return s;
        }

        /* eslint-disable @typescript-eslint/no-non-null-assertion */

        const passengerSelection = s.passengers.find(
          (p) => p.passengerKey === action.payload.passengers[0]!.passengerKey,
        );
        if (!passengerSelection) {
          return {
            ...s,
            passengers: s.passengers.concat(action.payload.passengers[0]),
          };
        }

        return {
          ...s,
          passengers: s.passengers.map((p) => {
            if (p.passengerKey !== action.payload.passengers[0]!.passengerKey) {
              return p;
            }

            // Can only have either Priority Check-in or Airport Check-in
            const paxSsrs =
              action.payload.passengers[0]!.ssrs[0]?.ssrCode ===
              ExtrasSsrsEnum.AIRPORT_CHECK_IN
                ? p.ssrs.filter(
                    (ssr) =>
                      ssr.ssrCode !== ExtrasSsrsEnum.PRIORITY_AIRPORT_CHECK_IN,
                  )
                : action.payload.passengers[0]!.ssrs[0]?.ssrCode ===
                  ExtrasSsrsEnum.PRIORITY_AIRPORT_CHECK_IN
                ? p.ssrs.filter(
                    (ssr) => ssr.ssrCode !== ExtrasSsrsEnum.AIRPORT_CHECK_IN,
                  )
                : p.ssrs;

            const ssrSelection = paxSsrs.find(
              (ssr) =>
                ssr.ssrCode === action.payload.passengers[0]!.ssrs[0]?.ssrCode,
            );
            if (!ssrSelection && action.payload.passengers[0]!.ssrs[0]) {
              return {
                ...p,
                ssrs: paxSsrs.concat(action.payload.passengers[0]!.ssrs[0]),
              };
            }

            return {
              ...p,
              ssrs: paxSsrs.map((ssr) => {
                if (
                  ssr.ssrCode !== action.payload.passengers[0]!.ssrs[0]?.ssrCode
                ) {
                  return ssr;
                }

                return action.payload.passengers[0]!.ssrs[0];
              }),
            };
          }),
        };

        /* eslint-enable @typescript-eslint/no-non-null-assertion */
      }),
    };
  }),

  on(extrasStoreExtrasUpdateRequestAllPax, (state, action): State => {
    if (!state.totalSelections) {
      return state;
    }

    const journeySelection = state.totalSelections.find(
      (s) => s.journeyKey === action.journeyKey,
    );
    if (!journeySelection) {
      return {
        ...state,
      };
    }

    return {
      ...state,
      totalSelections: state.totalSelections.map((s) => {
        if (s.journeyKey !== action.journeyKey) {
          return s;
        }

        return {
          ...s,
          passengers: s.passengers.map((p) => {
            // Can only have either Priority Check-in or Airport Check-in
            const paxSsrs =
              action.ssr.ssrCode === ExtrasSsrsEnum.AIRPORT_CHECK_IN
                ? p.ssrs.filter(
                    (ssr) =>
                      ssr.ssrCode !== ExtrasSsrsEnum.PRIORITY_AIRPORT_CHECK_IN,
                  )
                : action.ssr.ssrCode ===
                  ExtrasSsrsEnum.PRIORITY_AIRPORT_CHECK_IN
                ? p.ssrs.filter(
                    (ssr) => ssr.ssrCode !== ExtrasSsrsEnum.AIRPORT_CHECK_IN,
                  )
                : p.ssrs;

            const ssrSelection = paxSsrs.find(
              (ssr) => ssr.ssrCode === action.ssr.ssrCode,
            );
            if (!ssrSelection) {
              return {
                ...p,
                ssrs: paxSsrs.concat(action.ssr),
              };
            }

            return {
              ...p,
              ssrs: paxSsrs.map((ssr) => {
                if (ssr.ssrCode !== action.ssr.ssrCode) {
                  return ssr;
                }

                return action.ssr;
              }),
            };
          }),
        };
      }),
    };
  }),

  on(
    extrasClearTotalSelectionsState,
    availabilitySellTripSelectionSuccess,
    (state): State => ({
      ...state,
      totalSelections: null,
    }),
  ),
);

export const extrasReducer = (state: State, action: Action): State =>
  reducer(state, action);
